import {
  MAX_UPLOAD_BYTES,
  PlanExpiredError,
  useCreateNook,
  useUploadImage,
  type Capture,
} from "@nook/api-client"
import { formatBytes, normalizeUrl } from "@nook/client-ui"
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Progress,
  Textarea,
  toast,
} from "@nook/ui"
import { ImageUp, Loader2, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Hint } from "@/components/keys.js"
import { UpgradePrompt } from "@/components/upgrade-prompt.js"

/**
 * Capture, from the webapp (blocks 03, 06 and 07).
 *
 * PRD §5.1 promises "a URL, a note, an image, or any combination" on one nook,
 * and this dialog is where that promise is kept. It is also the whole argument
 * for the payload being a property bag and not a union — 06 added an image and
 * a property, 07 added a URL and a property, and nothing in between changed.
 *
 * The text box is one field with two jobs: on its own it is the nook's content,
 * and next to a URL or an image it is the annotation. One box, because
 * `user_note` is one column and a second one writing to the same place would be
 * two names for one thing.
 */

/** Opened by the shell: `N`, the library's New nook, or the palette. */
export function CaptureDialog({ open, onOpenChange: setOpen }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [url, setUrl] = useState("")
  // Shown on blur and on a rejected save, never while the field is still being
  // typed into — `h` is not a mistake, it is a beginning.
  const [urlInvalid, setUrlInvalid] = useState(false)
  const [text, setText] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [expired, setExpired] = useState(false)

  const create = useCreateNook()
  const upload = useUploadImage()
  const pending = create.isPending || upload.isPending

  // Revoked on replacement and on unmount: an object URL is a reference the
  // browser holds until told otherwise, and a modal that is opened fifty times
  // would otherwise pin fifty images in memory.
  useEffect(() => {
    if (!file) return setPreview(null)

    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  function choose(next: File | null | undefined) {
    if (!next) return

    if (!next.type.startsWith("image/")) {
      return toast.error("That is not an image.")
    }

    // Refused here as well as at `/v1/uploads/sign`, so a 25MB file costs a
    // message rather than a round-trip that was always going to be a `413`.
    if (next.size > MAX_UPLOAD_BYTES) {
      return toast.error(`Images are limited to ${formatBytes(MAX_UPLOAD_BYTES)}.`)
    }

    setFile(next)
  }

  function reset() {
    setUrl("")
    setUrlInvalid(false)
    setText("")
    setFile(null)
    setOpen(false)
  }

  function save() {
    const link = url.trim()
    const normalized = link ? normalizeUrl(link) : null

    if (link && !normalized) return setUrlInvalid(true)
    if (pending || (!file && !normalized && !text.trim())) return

    const body = text.trim()

    // Next to a URL or an image the text is the annotation and becomes
    // `user_note`; alone it is the nook itself and becomes `content` (D5).
    const capture: Capture =
      normalized || file
        ? { url: normalized ?? undefined, note: body || undefined }
        : { text }

    const done = {
      onSuccess: () => {
        reset()
        toast.success("Nooked ✓")
      },
      // Closed, not reset: the draft is still there if they come back paid.
      onError: (error: Error) => {
        if (!(error instanceof PlanExpiredError)) return toast.error(error.message)
        setOpen(false)
        setExpired(true)
      },
    }

    if (file) {
      upload.mutate({ file, capture }, done)
    } else {
      create.mutate(capture, done)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (pending) return
          setOpen(next)
          if (!next) setFile(null)
        }}
      >
        {/* Grows from the header's New nook button, top right. */}
        <DialogContent className="origin-[100%_0]">
          <DialogHeader>
            <DialogTitle>New nook</DialogTitle>
            <DialogDescription>
              Nook a link, a note or an image. One is enough, or combine them.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-1.5">
            <label htmlFor="capture-url" className="text-sm font-medium">
              Link
            </label>
            <Input
              id="capture-url"
              type="url"
              inputMode="url"
              autoComplete="off"
              autoFocus
              spellCheck={false}
              value={url}
              disabled={pending}
              placeholder="Paste a URL, like example.com/article"
              aria-invalid={urlInvalid}
              aria-describedby={urlInvalid ? "capture-url-error" : undefined}
              onChange={(event) => {
                setUrl(event.target.value)
                setUrlInvalid(false)
              }}
              onBlur={(event) => setUrlInvalid(Boolean(event.target.value.trim()) && !normalizeUrl(event.target.value.trim()))}
              onKeyDown={(event) => {
                if (event.key === "Enter") save()
              }}
            />

            {urlInvalid ? (
              <p id="capture-url-error" className="text-destructive text-xs">
                That does not look like a link.
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <label htmlFor="capture-note" className="text-sm font-medium">
              Note
            </label>
            <Textarea
              id="capture-note"
              rows={file || url ? 3 : 5}
              value={text}
              placeholder={file || url ? "Why are you saving this?" : "Write anything you want to remember"}
              onChange={(event) => setText(event.target.value)}
              // A link pasted into the note box was meant for the field above it —
              // people paste where the cursor is, not where the form wanted them.
              onPaste={(event) => {
                const pasted = event.clipboardData.getData("text").trim()

                if (!url && !text && pasted && normalizeUrl(pasted) && !/\s/.test(pasted)) {
                  event.preventDefault()
                  setUrl(pasted)
                  setUrlInvalid(false)
                }
              }}
              // ⌘/Ctrl-Enter saves, because a note composer whose only exit is the
              // mouse is a note composer people stop using.
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) save()
              }}
            />
          </div>

          <div className="grid gap-1.5">
            <span className="text-sm font-medium">Image</span>

            {preview ? (
              <div className="border-border relative overflow-hidden rounded-lg border">
                <img
                  src={preview}
                  alt={file?.name ?? "Selected image"}
                  className="max-h-56 w-full object-contain"
                />
                <Button
                  variant="secondary"
                  size="icon-sm"
                  aria-label="Remove image"
                  className="absolute top-2 right-2"
                  disabled={pending}
                  onClick={() => setFile(null)}
                >
                  <X />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                disabled={pending}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault()
                  choose(event.dataTransfer.files[0])
                }}
                className="border-border text-muted-foreground hover:border-ring hover:text-foreground flex items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
              >
                <ImageUp className="size-4 shrink-0" />
                <span>
                  <span className="text-foreground">Choose an image</span> or drop one here
                </span>
                <span className="ml-auto text-xs">Up to {formatBytes(MAX_UPLOAD_BYTES)}</span>
              </button>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              choose(event.target.files?.[0])
              // Cleared so that picking the same file twice in a row still fires.
              event.target.value = ""
            }}
          />

          {upload.progress === null ? null : (
            <Progress value={upload.progress} aria-label="Upload progress" />
          )}

          <DialogFooter className="sm:items-center">
            <span className="text-muted-foreground mr-auto hidden text-xs sm:flex">
              <Hint keys="$mod+Enter">to nook</Hint>
            </span>
            <DialogClose asChild>
              <Button variant="outline" disabled={pending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              disabled={pending || (!file && !url.trim() && !text.trim())}
              onClick={save}
            >
              {pending && <Loader2 className="animate-spin" />}
              Nook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradePrompt open={expired} onOpenChange={setExpired} />
    </>
  )
}
